import { useEffect, useState } from "react";
import { Link2, Copy, Trash2, Eye, Calendar, Lock, Check, X, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RECIPIENT_ROLES = [
  { value: "buyer", label: "Buyer" },
  { value: "conveyancer", label: "Conveyancer" },
  { value: "property_manager", label: "Property Manager" },
  { value: "family", label: "Family Member" },
  { value: "other", label: "Other" },
];

const EXPIRY_OPTIONS = [
  { value: "7d", label: "Expires in 7 days" },
  { value: "30d", label: "Expires in 30 days" },
  { value: "none", label: "No expiry (revoke any time)" },
];

/**
 * Share modal — owner creates a public share link for their property's Passport.
 */
export function ShareModal({ propertyId, onClose }) {
  const [recipientLabel, setRecipientLabel] = useState("");
  const [recipientRole, setRecipientRole] = useState("buyer");
  const [expiry, setExpiry] = useState("30d");
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createdShare, setCreatedShare] = useState(null);
  const [existingShares, setExistingShares] = useState([]);

  const loadExisting = async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/shares`);
      setExistingShares(data);
    } catch {}
  };

  useEffect(() => { loadExisting(); }, [propertyId]);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/shares`, {
        recipient_label: recipientLabel,
        recipient_role: recipientRole,
        expiry,
        password: usePassword && password ? password : null,
      });
      setCreatedShare(data);
      loadExisting();
      toast.success("Share link created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create share link");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/shared/passport/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const revoke = async (shareId) => {
    if (!window.confirm("Revoke this share link? The recipient will lose access immediately.")) return;
    try {
      await api.delete(`/properties/${propertyId}/shares/${shareId}`);
      toast.success("Share revoked");
      loadExisting();
    } catch (err) {
      toast.error("Revoke failed");
    }
  };

  return (
    <div data-testid="share-modal" className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-7 lg:p-8 my-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">Share AWARE Water Health Record</div>
            <h2 className="font-display mt-1 text-2xl font-bold text-slate-900">Create a share link</h2>
            <p className="text-sm text-slate-500 mt-1">View-only access. Recipient cannot edit, delete or transfer.</p>
          </div>
          <button data-testid="share-modal-close" onClick={onClose} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-900"><X className="w-4 h-4" /></button>
        </div>

        {!createdShare && (
          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="recipient_label" className="text-xs font-semibold text-slate-700">Recipient name (optional)</Label>
              <Input data-testid="share-recipient-label" id="recipient_label" value={recipientLabel} onChange={(e) => setRecipientLabel(e.target.value)} placeholder="e.g. Mary's Conveyancing" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Recipient role</Label>
              <Select value={recipientRole} onValueChange={setRecipientRole}>
                <SelectTrigger data-testid="share-recipient-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{RECIPIENT_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Expiry</Label>
              <Select value={expiry} onValueChange={setExpiry}>
                <SelectTrigger data-testid="share-expiry" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{EXPIRY_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label data-testid="share-password-toggle" className="flex items-center gap-2.5 text-sm font-medium text-slate-700 cursor-pointer select-none">
              <input type="checkbox" checked={usePassword} onChange={(e) => setUsePassword(e.target.checked)} className="rounded border-slate-300" />
              <Lock className="w-3.5 h-3.5 text-slate-500" /> Password-protect this link
            </label>
            {usePassword && (
              <Input data-testid="share-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Choose a password" className="text-sm" />
            )}

            <div className="flex gap-3 pt-2">
              <Button data-testid="share-create-btn" onClick={handleCreate} disabled={submitting} className="btn-aqua rounded-full font-semibold flex-1">
                {submitting ? "Creating..." : "Create share link"}
              </Button>
              <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
            </div>
          </div>
        )}

        {createdShare && (
          <div data-testid="share-created" className="mt-6">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-2.5">
              <Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-emerald-900">
                <div className="font-semibold">Share link ready</div>
                <p className="mt-0.5">Copy this link now — the full token is only shown once.</p>
              </div>
            </div>
            <div className="mt-4 rounded-2xl bg-slate-900 text-white p-4 font-mono text-xs break-all">
              {window.location.origin}/shared/passport/{createdShare.token}
            </div>
            <Button data-testid="share-copy-btn" onClick={() => copyLink(createdShare.token)} className="mt-3 btn-aqua rounded-full font-semibold w-full">
              <Copy className="w-4 h-4 mr-1.5" /> Copy link
            </Button>
            {createdShare.password_protected && (
              <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Recipient will need the password you set.</p>
            )}
            <Button variant="outline" onClick={() => { setCreatedShare(null); }} className="mt-3 rounded-full w-full">Create another</Button>
          </div>
        )}

        {/* Existing shares */}
        {existingShares.length > 0 && (
          <div className="mt-7 pt-6 border-t border-slate-200">
            <h3 className="font-display font-semibold text-slate-900 text-sm">Existing share links ({existingShares.length})</h3>
            <div className="mt-3 space-y-2">
              {existingShares.map((s) => (
                <div key={s.id} data-testid={`share-row-${s.id}`} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {s.recipient_label || RECIPIENT_ROLES.find((r) => r.value === s.recipient_role)?.label || "Share"}
                      {s.password_protected && <Lock className="inline w-3 h-3 ml-1.5 text-slate-500" />}
                    </div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                      <span>{s.token_preview}</span>
                      <span>·</span>
                      <span><Eye className="inline w-3 h-3 mr-0.5" />{s.access_count || 0} views</span>
                      <span>·</span>
                      <span>
                        {s.is_active ? (s.expires_at ? "expires " + formatDateAU(s.expires_at) : "no expiry") : "inactive"}
                      </span>
                    </div>
                  </div>
                  {s.is_active && (
                    <button data-testid={`share-revoke-${s.id}`} onClick={() => revoke(s.id)} className="p-1.5 rounded-full text-rose-500 hover:bg-rose-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Transfer modal — customer-initiated transfer request.
 */
export function TransferModal({ propertyId, propertyAddress, onClose, onSubmitted }) {
  const [toEmail, setToEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!confirmed) {
      toast.error("Please confirm you understand the transfer is permanent");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/properties/${propertyId}/transfer-request`, { to_email: toEmail, notes });
      toast.success("Transfer request submitted — an admin will review and approve.");
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transfer request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="transfer-modal" className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-7 lg:p-8 my-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-amber-700">Transfer property</div>
            <h2 className="font-display mt-1 text-2xl font-bold text-slate-900">Request transfer</h2>
            <p className="text-sm text-slate-500 mt-1">{propertyAddress}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-900"><X className="w-4 h-4" /></button>
        </div>

        <div className="mt-5 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <strong>Transfers are permanent.</strong> Once approved by an admin, the new owner will see the AWARE Water Health Record, test history, AI summaries and timeline. You will lose access to this property.
          </div>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-700">New owner's email</Label>
            <Input data-testid="transfer-to-email" type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="newowner@example.com" className="mt-1.5" />
            <p className="text-[11px] text-slate-500 mt-1.5">The new owner must already have an account with us. If they don't, ask them to sign up first.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Notes for admin (optional)</Label>
            <textarea data-testid="transfer-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Reason for transfer, sale details, etc." className="mt-1.5 w-full rounded-xl border-slate-200 text-sm p-3" />
          </div>
          <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer select-none">
            <input type="checkbox" data-testid="transfer-confirm" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 rounded border-slate-300" />
            <span>I understand the transfer is permanent and that an admin will need to approve it.</span>
          </label>

          <div className="flex gap-3 pt-2">
            <Button data-testid="transfer-submit" onClick={handleSubmit} disabled={submitting || !toEmail || !confirmed} className="btn-aqua rounded-full font-semibold flex-1">
              <Send className="w-4 h-4 mr-1.5" />
              {submitting ? "Submitting..." : "Submit transfer request"}
            </Button>
            <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
